/**
 * Round table shape. Renders the table circle plus chairs arranged evenly
 * around its perimeter based on `seats`, and a label. Chairs are purely
 * visual here — they are NOT separate LayoutObjects (PRD §6: chairs are
 * auto-attached to tables, never placed individually in V1).
 *
 * Performance note: this component subscribes to the editor store via a
 * narrow selector for just this object's id, not the whole objects array —
 * see ObjectRenderer.tsx for how that's wired. Dragging this table re-
 * renders only this shape, not all thirty tables on the canvas.
 */

import { Circle, Group, Text } from 'react-konva';
import { useState } from 'react';
import type Konva from 'konva';
import type { LayoutObject } from '../../../domain/types';
import { FEET_TO_PX } from '../canvasConstants';

interface RoundTableProps {
  object: LayoutObject;
  isSelected: boolean;
  onSelect: (e?: Konva.KonvaEventObject<Event>) => void;
  onDragEnd: (x: number, y: number) => void;
  /** See ObjectRenderer.tsx's prop doc — counter-rotates the label only,
   * against the room's current VIEW orientation specifically, so a
   * freshly-placed table's number is immediately readable without the
   * coordinator needing to manually rotate anything. Does not affect how
   * the label rotates relative to the table itself when the coordinator
   * manually rotates the table — that relationship is unchanged. */
  roomRotationDegrees: number;
}

export function RoundTable({ object, isSelected, onSelect, onDragEnd, roomRotationDegrees }: RoundTableProps) {
  const radiusPx = 2.5 * FEET_TO_PX; // 5ft diameter table
  const chairCount = object.seats ?? 8;
  const chairRadiusPx = radiusPx + 14; // chairs sit just outside the table edge
  // Shadows are visually nice at rest but measurably more expensive to
  // recompute every frame while actively dragging — a well-known Konva
  // performance idiom is to disable shadow rendering for the duration of
  // a drag and restore it on release, which keeps the drag motion itself
  // smooth without losing the resting-state polish.
  const [isDragging, setIsDragging] = useState(false);

  function handleDragStart(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(true);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grabbing';
  }

  function handleDragEnd(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(false);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grab';
    const node = e.target;
    onDragEnd(node.x() / FEET_TO_PX, node.y() / FEET_TO_PX);
  }

  return (
    <Group
      id={object.id}
      x={object.x * FEET_TO_PX}
      y={object.y * FEET_TO_PX}
      rotation={object.rotation}
      draggable={!object.locked}
      onClick={onSelect}
      onTap={onSelect}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      opacity={object.locked ? 0.85 : 1}
      onMouseEnter={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = object.locked ? 'default' : 'grab';
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = 'default';
      }}
    >
      {/* Chairs, evenly spaced around the perimeter */}
      {Array.from({ length: chairCount }).map((_, i) => {
        const angle = (i / chairCount) * Math.PI * 2;
        const cx = Math.cos(angle) * chairRadiusPx;
        const cy = Math.sin(angle) * chairRadiusPx;
        return (
          <Circle
            key={i}
            x={cx}
            y={cy}
            radius={6}
            fill="#FBF8F2"
            stroke="#C9A35C"
            strokeWidth={1}
          />
        );
      })}

      {/* Table surface */}
      <Circle
        radius={radiusPx}
        fill="#FFFEFC"
        stroke={isSelected ? '#C9A35C' : '#8FA38B'}
        strokeWidth={isSelected ? 3 : 1.5}
        shadowColor="#2A2522"
        shadowOpacity={isDragging ? 0 : 0.12}
        shadowBlur={isDragging ? 0 : 6}
        shadowOffsetY={isDragging ? 0 : 2}
      />

      <Text
        text={object.label}
        fontSize={11}
        fontFamily="Inter, sans-serif"
        fill="#4D453C"
        width={radiusPx * 2}
        height={radiusPx * 2}
        offsetX={radiusPx}
        offsetY={radiusPx}
        rotation={-roomRotationDegrees}
        align="center"
        verticalAlign="middle"
        listening={false}
      />

      {object.locked && (
        <Text
          text={'\u{1F512}'}
          fontSize={13}
          x={0}
          y={-(chairRadiusPx + 14)}
          offsetX={6.5}
          offsetY={6.5}
          listening={false}
        />
      )}
    </Group>
  );
}
